
import {Component, Inject, Prop, Provide, Vue, Watch} from "vue-property-decorator";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {mapState} from "vuex";

import vToggleSwitch from "./vToggleSwitch.vue";
import vIngredientsDisplayNames from "./vIngredientsDisplayNames.vue";
import vUSContainsPercentOfForm from "./vUSContainsPercentOfForm.vue";
import vAccordion from "./vAccordion.vue";
import {ExportLabelState} from "@/models/NutritionLabels/ExportLabelState";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {Recipe} from "@/models/Recipes/Recipe";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {debounce} from "@/utils/Helpers";
import {DietaryTypes} from "@/models/NutritionLabels/DietaryTypes";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import IngredientDisplay, {
  IngredientAllergenFormattingFontFamily,
  IngredientAllergenFormattingFontSize,
  IngredientAllergenFormattingStyle,
  IngredientAllergenVisualState
} from "@/models/Ingredient/IngredientDisplay";
import FreeTextPlaceholder from "@/models/NutritionLabels/FreeTextPlaceholder";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import VBanner from "@/components/vBanner.vue";
import vCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {buildComplexIngredientName, buildSimpleIngredientName} from "@/services/ExportLabel.service";
import VCard from "@/components/bootstrap/vCard.vue";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import LabelTypeSelection from "@/components/labelConfiguration/LabelTypeSelection.vue";
import LabelConfiguration from "@/components/labelConfiguration/LabelConfiguration.vue";
import FreeTextFieldsConfiguration from "@/components/labelConfiguration/FreeTextFieldsConfiguration.vue";
import CollapsableCardWithHeader from "@/components/bootstrap/CollapsableCardWithHeader.vue";
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import DietaryConfiguration from "@/components/labelConfiguration/DietaryConfiguration.vue";
import {LabelRenderConfiguration} from "@/models/NutritionLabels/v2/LabelRenderConfiguration";
import {LabelConfigurationResponse} from "@/models/NutritionLabels/v2/LabelConfigurationResponse";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {LabelRenderModel} from "@/models/NutritionLabels/v2/LabelRenderModel";
import {LabelConfigurationModel} from "@/models/NutritionLabels/v2/LabelConfigurationModel";
import {LabelIngredientConfiguration} from "@/models/NutritionLabels/v2/LabelIngredientConfiguration";
import {FreeTextFieldConfiguration} from "@/models/NutritionLabels/v2/FreeTextFieldConfiguration";
import {LabelRenderDto} from "@/models/NutritionLabels/v2/RenderLabelDto";
import {NutritionLabelDownloadFileFormat} from "@/models/NutritionLabels/NutritionLabelDownloadFileFormat";
import {FreeTextAttributeType} from "@/models/NutritionLabels/v2/enums/FreeTextAttributeType";
import {SaveCustomizedLabelDataDto} from "@/models/NutritionLabels/v2/SaveCustomizedLabelDataDto";
import {vSelectOptionBase} from "@/typings/common";
import {parseCountryKey, splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import IngredientConfiguration from "@/components/labelConfiguration/IngredientConfiguration.vue";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import {NutrientSource} from "@/models/NutrientSource/NutrientSource";
import {
  getCoreNutrients, getCoreSupplementNutrients,
  getSupplementNutrients,
  getVoluntaryNutrients
} from "@/models/NutritionLabels/v2/collections/NutrientGroups";
import { type BelowLabelConfiguration } from "@/models/NutritionLabels/v2/BelowLabelConfiguration";
import {NutritionCountry} from "@/models/NutritionLabels/NutritionCountry";
import TextEditorConfiguration from "@/components/labelConfiguration/TextEditorConfiguration.vue";
import { BrandingConfig } from "@/models/NutritionLabels/BrandingConfig";
import {AllergenSelectionStatus, SavedAllergen} from "@/models/Allergen/SelectedAllergen";
import {normalizeAllergenSelectionStatus} from "@/models/Allergen/AllergenSelectionStatusValue";
import {isUserOverridePrediction} from "@/components/allergens/allergenOverrideMatrixLogic";
import AllergenRepository from "@/utils/API/AllergenRepository";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import {AllergenDefinition} from "@/models/Allergen/AllergenDefinition";
import { getLabelRoundingProfileFilter, isSupplementLabelTemplate } from "@/utils/exportLabelRules";

type SavedLabelRenderModel = LabelRenderModel & {
  freeTextFields?: FreeTextFieldConfiguration | FreeTextPlaceholder[];
};

type ParsedLabelRenderModel = Omit<SavedLabelRenderModel, "freeTextFields"> & {
  freeTextFields?: FreeTextPlaceholder[];
};

type IngredientAllergenManualOverride = {
  isAllergen: boolean;
  visualState: IngredientAllergenVisualState;
};

const DefaultIngredientAllergenFormattingColor = "#C62828";
const DefaultIngredientAllergenHighlightColor = "#FFF176";
const DefaultIngredientAllergenFontFamily: IngredientAllergenFormattingFontFamily = "Arial";
const DefaultIngredientAllergenFontSize: IngredientAllergenFormattingFontSize = "7pt";

@Component({
  components: {
    VUpsellModal,
    TextEditorConfiguration,
    DietaryConfiguration,
    FreeTextFieldsConfiguration,
    LabelConfiguration,
    IngredientConfiguration,
    LabelTypeSelection,
    VCard,
    VRecipeNutritionLabelPrintout,
    VBanner,
    vToggleSwitch,
    vIngredientsDisplayNames,
    vUSContainsPercentOfForm,
    vAccordion,
    vCautionaryInputs,
    CollapsableCardWithHeader,
    AllergensConfiguration,
  },
  computed: {
    DietaryTypes() {
      return DietaryTypes;
    },
    LabelLanguage() {
      return LabelLanguage;
    },
    NutrientType() {
      return NutrientType;
    },
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
export default class vBackOfPackageConfig extends Vue {
  LabelTypeMetadata = LabelTypeMetadata;
  localizationState!: LocalizationState;
  accountState!: any;

  ukAllergenGuidanceUrl = "https://www.food.gov.uk/business-guidance/food-allergen-labelling-and-information-requirements-technical-guidance-part-1-guidance-for-businesses-providing-prepacked-food";

  @Prop() recipe!: Recipe;
  @Prop() packageLabelType!: LabelTypeMetadata;
  @Prop() preselectedLabelType: LabelTypeMetadata;
  @Prop({ default: "recipe" }) aiTargetType!: "recipe" | "combined-recipe";

  /**
   * Rounding rule system to use for the given country
   */
  @Prop() roundingRuleIndex!: number;

  @Prop({
    default: (): RecipeCompositionMeasurement[] => {
      return [];
    }
  }) subrecipeMeasurements!: RecipeCompositionMeasurement[];

  @Inject("fetchPreview") fetchPreviewV2: (renderData: LabelRenderDto, requestingScreen: ExportLabelView) => Promise<Blob>;
  @Inject("bopConfigRef") readonly bopConfigRef!: any;

  savedLabelConfig: SaveCustomizedLabelDataDto = null;
  hasResolvedSavedLabelLookup = false;
  savedLabelConfigLoadPromise: Promise<void> | null = null;
  model: ExportLabelState = new ExportLabelState();
  alreadyApplied = false;
  hasSavedData = false;
  showTrialUpsellModal = false;
  defaultTrialUpsellMessage = "Please subscribe now to access the full features/functionalities on this 'Export Label' section.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  isApplyingIngredientAiAllergens = false;
  hasAppliedBackOfPackageAiAnalysisFlag = false;
  lastBackOfPackageAiAnalysisResult: MlAnalysisResponse | null = null;
  ingredientAiAllergenApplyRequestToken = 0;
  shouldExpandAllergensConfiguration = false;

  subRecipeDisplays: IngredientDisplay[] = [];
  selectedLabelSubType: vSelectOptionBase<string> = null;
  manualIngredientAllergenOverrides = new Map<string, IngredientAllergenManualOverride>();
  selectedAllergens: SavedAllergen[] = [];
  allergenDefinitionIndex = new Map<number, AllergenDefinition>();
  countryAllergenDefinitions: AllergenDefinition[] = [];
  visibleAllergenDefinitions: AllergenDefinition[] = [];
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  selectedDietaryTypes: DietaryTypes[] = [];
  brandingConfiguration: BrandingConfig = {
    showLogo: false,
    logoPosition: "top-left",
    textTop: "",
    textBottom: "",
    showImage: false,
    imagePosition: "bottom-left"
  };
  requestedLabelConfigurations: string[] = [];
  config: LabelRenderConfiguration = {
    recipes: { enabled: false, mode: null },
    nutrients: { sugarAlcohols: false, voluntary: false, supplement: false },
    dimensions: { width: false },
  };
  labelConfiguration: LabelConfigurationModel = {
    hideOtherText: false,
    showShortFooter: false,
    showSideBySidePanel: false,
    useMcgUnit: false,
    switchToElectrolyteMode: false,
    showCarbsDV: false,
    combineVoluntaryNutrients: false,
    useCondensedFont: false,
    useInlineFootnote: false,
    widthCm: 0,
    selectedCoreNutrients: [],
    selectedSupplementNutrients: [],
    selectedVoluntaryNutrients: [],
    useCondensedFormat: false,
    hideTotalText: false,
    switchCalorieBreakdownFootnote: false,
    switchServingSizeText: false,
    includeMonoPoly: false,
    includeOmegas: false,
    includeTextualIndicators: false,
    includeTrafficLightColoring: false,
    showProteinDV: false,
    shouldDisplayCalories449Text: false,
    shouldDisplayNutrientsSideBySide: false,
    showCalorieFromFatText: false,
    shouldDisplaySugarAlcohol: false,
    showThcLogo: false,
    labelTypes: {},
    includeRi: false,
    includeSaltFootnote: false,
    includePerServingAnalysis: false,
    useParForFrenchServingSize: false,
    proprietaryBlends: [],
    recipes: [],
    useNormalServingSizeFont: false,
    displayValueforCoreNutrients: false,
    showSupplementProteinDV: false,
    hideCanadaSupplementFootnote: false,
    hideBorders: false,
  };

  freeTextFields: FreeTextPlaceholder[] = [];

  labelIngredientConfiguration = new LabelIngredientConfiguration();

  supplementNutrients = [
    NutrientType.Biotin,
    NutrientType.Choline,
    NutrientType.Niacin,
    NutrientType.PantothenicAcid,
    NutrientType.Riboflavin,
    NutrientType.Thiamine,
    NutrientType.VitaminA,
    NutrientType.VitaminB6,
    NutrientType.VitaminB12,
    NutrientType.VitaminC,
    NutrientType.VitaminD,
    NutrientType.VitaminE,
    NutrientType.Calcium,
    NutrientType.Chromium,
    NutrientType.Copper,
    NutrientType.Iodine,
    NutrientType.Magnesium,
    NutrientType.Molybdenum,
    NutrientType.Phosphorus,
    NutrientType.Potassium,
    NutrientType.Selenium,
    NutrientType.Zinc,
    NutrientType.LAlanine,
    NutrientType.LArginine,
    NutrientType.LAsparagine,
    NutrientType.LAsparticAcid,
    NutrientType.LCysteine,
    NutrientType.LGlutamicAcid,
    NutrientType.LGlutamine,
    NutrientType.Glycine,
    NutrientType.LHistidine,
    NutrientType.LIsoleucine,
    NutrientType.LLeucine,
    NutrientType.LLysine,
    NutrientType.LMethionine,
    NutrientType.LPhenylalanine,
    NutrientType.LProline,
    NutrientType.LSerine,
    NutrientType.LThreonine,
    NutrientType.LTyrosine,
    NutrientType.LValine,
    NutrientType.Caffeine
  ];

  hasNormalizedFreeTextPlaceholders = false;
  hasAutoAppliedFsisDefaults = false;

  fsisDefaultLabelConfiguration = {
    switchServingSizeText: true,
    showCalorieFromFatText: true,
    hideTotalText: true,
    switchCalorieBreakdownFootnote: true,
    useNormalServingSizeFont: true,
    shouldDisplayNutrientsSideBySide: true,
  };

  @Watch("savedLabelConfig", { immediate: true })
  async onSavedLabelConfigLoaded(val: SaveCustomizedLabelDataDto | null) {
    this.savedLabelConfigLoadPromise = this.applySavedLabelConfig(val);
    await this.savedLabelConfigLoadPromise;
  }

  async applySavedLabelConfig(val: SaveCustomizedLabelDataDto | null) {

    this.hasSavedData = !!val;
    this.hasResolvedSavedLabelLookup = false;

    if(!this.hasSavedData){
      this.labelConfiguration.selectedCoreNutrients = getCoreNutrients(this.recipe.nutritionCountry.id, this.roundingRuleIndex);
    }
    if (!val) return;

    this.alreadyApplied = false;
    this.hasAutoAppliedFsisDefaults = false;
    this.hasAppliedBackOfPackageAiAnalysisFlag = false;
    this.lastBackOfPackageAiAnalysisResult = null;

    void this.parsedModelData;

    this.alreadyApplied = true;
    this.setCountryBasedLanguage(this.recipe.nutritionCountry.name);
    await this.loadSavedAllergenConfiguration();
  }

  @Watch("model.freeTextPlaceholders")
  initFreeTextFields() {
    if (!this.hasNormalizedFreeTextPlaceholders) {
      const savedFreeTextFields: FreeTextPlaceholder[] = this.parsedModelData.freeTextFields ?? [];
      const labelFreeTextFields: FreeTextPlaceholder[] = this.model.freeTextPlaceholders ?? [];

      if (!labelFreeTextFields.length) return;

      // Anchor to the fields registered for the current label type, hydrating saved content
      // into each registered field where available.
      const mergedRegisteredFields = labelFreeTextFields.map((ftf) => {
        const saved = savedFreeTextFields.find((s) => s.displayText === ftf.displayText);
        return saved ? { ...ftf, content: saved.content ?? ftf.content } : ftf;
      });

      // Also carry forward persisted fields that are NOT registered for the current label
      // type but have non-empty content, so user-entered values survive a round-trip back
      // to the original label type (e.g. UK DURABILITY_INDICATION).
      const registeredDisplayNames = new Set(labelFreeTextFields.map((ftf) => ftf.displayText));
      const extraPersistedFields = savedFreeTextFields.filter(
        (ftf) => !registeredDisplayNames.has(ftf.displayText) && (ftf.content ?? []).length > 0
      );

      this.freeTextFields = [
        ...mergedRegisteredFields,
        ...extraPersistedFields,
      ];

      this.hasNormalizedFreeTextPlaceholders = true;
      this.fetchPreview();
    }
  }

  @Watch("model.labelTypes", { deep: true })
  onCautionarySopDataChanged(newVal: any) {
    this.$emit("csopDataChanged", this.model.labelTypes);
  }

  @Watch("selectedLabelSubType")
  onLabelTypeChange() {
    this.applyFsisDefaultsIfNeeded();

    this.updateRecipeMetadataChange(
      isSupplementLabelTemplate(this.selectedLabelSubType?.value, this.preselectedLabelType)
    );
  }

  isSavedFsisConfiguration() {
    const savedModel = this.parsedModelData;

    return savedModel?.switchServingSizeText === true
      && savedModel?.showCalorieFromFatText === true
      && savedModel?.hideTotalText === true
      && savedModel?.switchCalorieBreakdownFootnote === true
      && savedModel?.useNormalServingSizeFont === true
      && savedModel?.shouldDisplayNutrientsSideBySide === true;
  }

  isSavedFsisLabelContext() {
    const savedModel = this.parsedModelData as any;

    // New persisted marker takes precedence; fallback keeps compatibility with older saves.
    return savedModel?.isFsisLabel === true || this.isSavedFsisConfiguration();
  }

  applyFsisDefaultsIfNeeded() {
    if (this.hasAutoAppliedFsisDefaults) {
      return;
    }

    if (this.preselectedLabelType !== LabelTypeMetadata.FSIS) {
      return;
    }

    if (this.hasSavedData && this.isSavedFsisLabelContext()) {
      return;
    }

    this.labelConfiguration = {
      ...this.labelConfiguration,
      ...this.fsisDefaultLabelConfiguration,
    };

    this.hasAutoAppliedFsisDefaults = true;
  }

  @Watch("recipe.nutritionCountry", { immediate: true, deep: true })
  onCountryChange(newCountry: NutritionCountry | null) {
    if (!newCountry) return;
    const countryName = newCountry.name;

    this.setCountryBasedLanguage(countryName);
  }

  @Watch("selectedAllergens", { deep: true })
  onSelectedAllergensChanged() {
    this.reapplyBackOfPackageAiVisualState();
  }

  setCountryBasedLanguage(countryName : string){
    if (countryName === "UK") {
      this.model.primaryLanguage = LabelLanguage.English;
    }
    else {
      // Other countries
      const savedLanguage: LabelLanguage | undefined = this.parsedModelData?.language;

      if (savedLanguage) {
        this.model.primaryLanguage = savedLanguage;
      }
      else if (!this.model.primaryLanguage) {
        this.model.primaryLanguage = LabelLanguage.English;
      }
    }
    this.$emit("languageChange", this.model.primaryLanguage);
  }

  // Handle new label flow, no prior saved label
  async created() {
    this.labelIngredientConfiguration.shouldExpandSubrecipes = this.recipe.nutritionCountry.id == NutritionLabelCountry.UK;
    this.ingredientAndSubRecipeDisplaysInit();
    await this.loadAllergenDefinitions();
    await this.loadSavedAllergenConfiguration();
  }

  get translations() {
    return this.localizationState.translations;
  }

  get isTrialUser() {
    return !!this.$store.getters.getUserDetails.isTrial;
  }

  get isAllergenAiAddonActive() {
    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get showAllergenAiButton() {
    return this.isTrialUser || this.isAllergenAiAddonActive;
  }

  get hasAppliedBackOfPackageAiAnalysis() {
    return this.hasAppliedBackOfPackageAiAnalysisFlag;
  }

  get hasVisibleBackOfPackageAiState() {
    return this.hasAppliedBackOfPackageAiAnalysis || this.hasSavedAllergenAiProvenance;
  }

  get hasSavedAllergenAiProvenance() {
    return (this.selectedAllergens || []).some((allergen: any) => {
      if (!allergen || typeof allergen !== "object") {
        return false;
      }

      const normalizedStatus = normalizeAllergenSelectionStatus(allergen.status);

      return normalizedStatus === AllergenSelectionStatus.DetectedByAI
        || Boolean(allergen.aiRecipeAggregateId);
    });
  }

  get isHydrated() {
    return this.hasResolvedSavedLabelLookup;
  }

  get hasOnlyCautionary(){
    if(this.model.hasValidCautionary && this.packageLabelType != LabelTypeMetadata.FrontOfPack){
      return true;
    }
  }

  get allergenGuidancePopoverContent() {
    return `For your ready reference on UK Food Allergen Labelling and Information, please <a href="${this.ukAllergenGuidanceUrl}" target="_blank" rel="noopener noreferrer">click here</a>.`;
  }

  get shouldShowUkAllergenGuidance() {
    return this.recipe?.nutritionCountry?.id === NutritionLabelCountry.UK;
  }

  get ingredientAndSubRecipeDisplays(){
    if(this.labelIngredientConfiguration.shouldExpandSubrecipes) {
      return this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList;
    }

    else {
      return this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList;
    }
  }

  get savedLabelType() {
    this.applyFsisDefaultsIfNeeded();

    if (this.savedLabelConfig?.labelTemplate) {
      // Keep the exact saved template so variant supplement labels re-select correctly.
      return this.savedLabelConfig.labelTemplate;
    }

    switch (this.preselectedLabelType) {
    case LabelTypeMetadata.SupplementFacts:
      return "SupplementLabel";

    case LabelTypeMetadata.FSIS:
      return "StandardVerticalLabel";

    default:
      return "StandardVerticalLabel";
    }
  }

  get shouldExpandLabelConfiguration() {
    const isAggregateOrDualColumn = this.selectedLabelSubType?.value === 'AggregateLabel'
      || this.selectedLabelSubType?.value === 'DualColumnLabel';

    const hasRecipeRequirement = this.requestedLabelConfigurations.includes('recipes.arbitrary')
      || this.requestedLabelConfigurations.includes('recipes.two');

    return isAggregateOrDualColumn || hasRecipeRequirement;
  }

  get labelRoundingProfileFilter() {
    return getLabelRoundingProfileFilter({
      selectedLabelTypeValue: this.selectedLabelSubType?.value,
      preselectedLabelType: this.preselectedLabelType,
      countryId: this.recipe?.nutritionCountry?.id,
      roundingRuleIndex: this.roundingRuleIndex,
    });
  }

  normalizeFreeTextFields(
    freeTextFields?: FreeTextFieldConfiguration | FreeTextPlaceholder[] | null
  ): FreeTextPlaceholder[] {
    if (Array.isArray(freeTextFields)) {
      return freeTextFields;
    }

    if (!freeTextFields) {
      return [];
    }

    // Convert the persisted object shape to a flat array. All keys are kept here so that
    // callers (initFreeTextFields, requestLabelConfigurations) can decide independently
    // which of them to surface — registered fields for the current label type are always
    // shown; non-registered persisted keys with non-empty content are preserved so users
    // don't lose data if they switch back to the original label type (e.g. UK fields).
    return Object.entries(freeTextFields)
      .map(([displayText, content], index) => ({
        id: index + 1,
        displayText: displayText as FreeTextAttributeType,
        content: content || []
      }));
  }

  get parsedModelData(): ParsedLabelRenderModel {
    const data = this.savedLabelConfig?.modelData;

    if (!data) return {} as ParsedLabelRenderModel;

    let parsedModel: SavedLabelRenderModel;

    if (typeof data === "string") {
      try {
        parsedModel = JSON.parse(data) as SavedLabelRenderModel;
      }
      catch (err) {
        console.error(err);
        return {} as ParsedLabelRenderModel;
      }
    }
    else {
      parsedModel = { ...data } as SavedLabelRenderModel;
    }

    const normalizedModel: ParsedLabelRenderModel = {
      ...parsedModel,
      freeTextFields: this.normalizeFreeTextFields(parsedModel.freeTextFields)
    };

    this.labelConfiguration.selectedCoreNutrients = normalizedModel.coreNutrients || [];
    this.labelConfiguration.selectedSupplementNutrients = normalizedModel.supplementNutrients?.filter((n: NutrientType) =>
      getSupplementNutrients(this.recipe.nutritionCountry.id).includes(n)
    ) || [];
    const availableVoluntaryNutrients = getVoluntaryNutrients(
      this.recipe.nutritionCountry.id,
      this.roundingRuleIndex,
      this.selectedLabelSubType?.value
    );
    this.labelConfiguration.selectedVoluntaryNutrients = normalizedModel.voluntaryNutrients?.filter((n: NutrientType) =>
      availableVoluntaryNutrients.includes(n)
    ) || [];

    if (!this.alreadyApplied) {
      this.model.primaryLanguage = normalizedModel.language;
      this.labelConfiguration = {
        ...this.labelConfiguration,
        ...normalizedModel
      };

      if (normalizedModel.brandingConfiguration) {
        this.brandingConfiguration = {
          showLogo: normalizedModel.brandingConfiguration.showLogo ?? false,
          logoPosition: normalizedModel.brandingConfiguration.logoPosition ?? "top-left",
          textTop: normalizedModel.brandingConfiguration.textTop ?? {},
          textBottom: normalizedModel.brandingConfiguration.textBottom ?? {},
          showImage: normalizedModel.brandingConfiguration.showImage ?? false,
          imagePosition: normalizedModel.brandingConfiguration.imagePosition ?? "bottom-left",
        };
      }

      if (normalizedModel.belowLabelConfiguration) {
        const blc = normalizedModel.belowLabelConfiguration;

        this.selectedAllergens = this.isTrialUser
          ? []
          : this.normalizeSelectedAllergensWithDefinitions(blc.allergens || []);
        this.mayContainText = this.convertMayContainArrayToMap(
          blc.mayContain || [],
          normalizedModel.language
        );
        this.selectedDietaryTypes = blc.dietaryLabels || [];

        this.labelIngredientConfiguration.includeIngredients = blc.shouldDisplayIngredients ?? this.labelIngredientConfiguration.includeIngredients;
        this.labelIngredientConfiguration.shouldExpandSubrecipes = blc.shouldExpandSubrecipes ?? this.labelIngredientConfiguration.shouldExpandSubrecipes;

        if (blc.ingredientDisplayModels) {
          this.populateSavedDisplays(blc);
        }
      }
      this.alreadyApplied = true;
    }

    return normalizedModel;
  }

  shouldPreferStoredExportLabelConfiguration() {
    return Boolean(this.savedLabelConfig?.id || this.savedLabelConfig?.modelData);
  }

  shouldHydrateAiDefaultsForStoredConfiguration() {
    const storedDisplays = this.parsedModelData?.belowLabelConfiguration?.ingredientDisplayModels || [];

    if (!storedDisplays.length) {
      return true;
    }

    return !storedDisplays.some((display: IngredientDisplay) => {
      const visualState = display?.allergenVisualState || "default";
      return Boolean(display?.isAllergen) || visualState !== "default";
    });
  }

  shouldHydrateAiDefaultsForCurrentExpandedView() {
    if (!this.labelIngredientConfiguration.shouldExpandSubrecipes) {
      return false;
    }

    const expandedIngredientDisplays = this.labelIngredientConfiguration
      .ingredientAndSubRecipeDisplaysModel
      .expandedSubRecipeList
      .filter((display) => Boolean(display?.ingredientId));

    if (!expandedIngredientDisplays.length) {
      return false;
    }

    return !expandedIngredientDisplays.some((display) => {
      const visualState = display?.allergenVisualState || "default";
      return Boolean(display?.isAllergen) || visualState !== "default";
    });
  }

  async onSavedLabelLookupCompleted() {
    if (this.savedLabelConfigLoadPromise) {
      await this.savedLabelConfigLoadPromise;
    }

    if (this.hasResolvedSavedLabelLookup) {
      return;
    }

    this.hasResolvedSavedLabelLookup = true;

    if (this.shouldPreferStoredExportLabelConfiguration()) {
      if (
        this.shouldHydrateAiDefaultsForStoredConfiguration()
        || this.shouldHydrateAiDefaultsForCurrentExpandedView()
      ) {
        await this.hydrateBackOfPackageAiAnalysis();
      }
      else {
        this.hasAppliedBackOfPackageAiAnalysisFlag = false;
        this.lastBackOfPackageAiAnalysisResult = null;
      }
      return;
    }

    await this.hydrateBackOfPackageAiAnalysis();
  }

  convertMayContainArrayToMap(
    value: string[][],
    language: keyof typeof LabelLanguage
  ): Partial<Record<LabelLanguage, string[]>> {
    const langs = splitLanguages(LabelLanguage[language]) as LabelLanguage[];

    const result: Partial<Record<LabelLanguage, string[]>> = {};

    langs.forEach((lang, index) => {
      result[lang] = [...(value[index] ?? [])];
    });

    return result;
  }

  populateSavedDisplays(blc: BelowLabelConfiguration) {
    const mergeSavedDisplayText = (existingDisplay: IngredientDisplay, savedDisplay: IngredientDisplay) => ({
      ...existingDisplay,
      displayTextInPrimaryLanguage: savedDisplay.displayTextInPrimaryLanguage ?? existingDisplay.displayTextInPrimaryLanguage,
      displayTextInSecondaryLanguage: savedDisplay.displayTextInSecondaryLanguage ?? existingDisplay.displayTextInSecondaryLanguage,
      displayText: savedDisplay.displayText ?? existingDisplay.displayText,
      isAllergen: savedDisplay.isAllergen ?? existingDisplay.isAllergen,
      allergenVisualState: savedDisplay.allergenVisualState ?? existingDisplay.allergenVisualState,
      allergenFormattingStyle: savedDisplay.allergenFormattingStyle ?? existingDisplay.allergenFormattingStyle ?? "bold",
      allergenFormattingColor: savedDisplay.allergenFormattingColor ?? existingDisplay.allergenFormattingColor ?? DefaultIngredientAllergenFormattingColor,
      allergenFormattingHighlightColor: savedDisplay.allergenFormattingHighlightColor ?? existingDisplay.allergenFormattingHighlightColor ?? DefaultIngredientAllergenHighlightColor,
      allergenFormattingFontFamily: savedDisplay.allergenFormattingFontFamily ?? existingDisplay.allergenFormattingFontFamily ?? DefaultIngredientAllergenFontFamily,
      allergenFormattingFontSize: savedDisplay.allergenFormattingFontSize ?? existingDisplay.allergenFormattingFontSize ?? DefaultIngredientAllergenFontSize,
    });

    if (this.labelIngredientConfiguration.shouldExpandSubrecipes) {
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList =
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.map((existingDisplay) => {
          const savedDisplay = blc.ingredientDisplayModels.find((item) =>
            Boolean(item.ingredientId) && item.ingredientId === existingDisplay.ingredientId
          );

          return savedDisplay
            ? mergeSavedDisplayText(existingDisplay, savedDisplay)
            : existingDisplay;
        });
    }

    else {
      const ingredientsOnly = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.filter(
        (itm) => Boolean(itm.ingredientId)
      );
      const subrecipesOnly = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.filter(
        (itm) => Boolean(itm.recipeId)
      );

      const savedIngredientsOnly = blc.ingredientDisplayModels.filter((itm) => (
        Boolean(itm.ingredientId)
        && ingredientsOnly.map((disp) => disp.ingredientId).includes(itm.ingredientId)
      ));

      const savedRecipesOnly = blc.ingredientDisplayModels.filter((itm) => (
        Boolean(itm.recipeId)
        && subrecipesOnly.map((disp) => disp.recipeId).includes(itm.recipeId)
      ));

      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = [
        ...ingredientsOnly.map((existingDisplay) => {
          const savedDisplay = savedIngredientsOnly.find((item) => item.ingredientId === existingDisplay.ingredientId);
          return savedDisplay
            ? mergeSavedDisplayText(existingDisplay, savedDisplay)
            : existingDisplay;
        }),
        ...subrecipesOnly.map((existingDisplay) => {
          const savedDisplay = savedRecipesOnly.find((item) => item.recipeId === existingDisplay.recipeId);
          return savedDisplay
            ? mergeSavedDisplayText(existingDisplay, savedDisplay)
            : existingDisplay;
        })
      ];
    }

    this.captureSavedManualIngredientAllergenOverrides(blc.ingredientDisplayModels || []);
  }

  handleMayContainUpdate(
    updated: Partial<Record<LabelLanguage, string[]>>
  ) {
    const activeLanguages = splitLanguages(
      LabelLanguage[this.model.primaryLanguage]
    ) as LabelLanguage[];

    const cleaned: Partial<Record<LabelLanguage, string[]>> = {};

    for (const lang of activeLanguages) {
      if (updated[lang]) {
        cleaned[lang] = [...updated[lang]];
      }
    }

    this.mayContainText = cleaned;
  }

  async loadAllergenDefinitions() {
    try {
      const [countryResponse, visibleResponse] = await Promise.all([
        AllergenRepository.listAllergensByCountry(this.recipe.nutritionCountry.id),
        AllergenRepository.listAllergens()
      ]);

      this.countryAllergenDefinitions = countryResponse.data || [];
      this.visibleAllergenDefinitions = visibleResponse.data || [];

      const definitions = [
        ...this.countryAllergenDefinitions,
        ...this.visibleAllergenDefinitions
      ];

      this.allergenDefinitionIndex = new Map(
        definitions.map((definition) => [definition.id, definition])
      );
    }
    catch {
      this.allergenDefinitionIndex = new Map();
      this.countryAllergenDefinitions = [];
      this.visibleAllergenDefinitions = [];
    }
  }

  normalizeSelectedAllergensWithDefinitions(savedAllergens: SavedAllergen[] = []) {
    return (savedAllergens || []).map((savedAllergen: any) => {
      if (!savedAllergen || typeof savedAllergen !== "object") {
        return savedAllergen;
      }

      const allergenDefinition = this.resolveStoredAllergenDefinition(savedAllergen);

      return {
        ...savedAllergen,
        id: allergenDefinition?.id || savedAllergen.id,
        code: savedAllergen.code || allergenDefinition?.code,
        name: savedAllergen.name || allergenDefinition?.name
      };
    });
  }

  resolveStoredAllergenDefinition(savedAllergen: Partial<SavedAllergen & AllergenDefinition>) {
    const directDefinition = this.allergenDefinitionIndex.get(Number(savedAllergen.id));

    if (this.recipe?.nutritionCountry?.id !== NutritionLabelCountry.UK) {
      return directDefinition;
    }

    const definitions = [...this.countryAllergenDefinitions, ...this.visibleAllergenDefinitions];
    const normalizedSavedCode = this.normalizeAnalysisSourceAllergenCode(
      String(savedAllergen.code || savedAllergen.name || directDefinition?.code || directDefinition?.name || "")
    );

    if (directDefinition && !this.shouldHideStoredAllergenForCountry(directDefinition)) {
      return directDefinition;
    }

    return definitions.find((definition) =>
      !this.shouldHideStoredAllergenForCountry(definition)
      && this.normalizeAnalysisSourceAllergenCode(definition.code || definition.name || "") === normalizedSavedCode
    ) || directDefinition;
  }

  shouldHideStoredAllergenForCountry(allergen: AllergenDefinition | null | undefined) {
    if (!allergen || this.recipe?.nutritionCountry?.id !== NutritionLabelCountry.UK) {
      return false;
    }

    const normalizedName = this.normalizeAllergenCode(allergen.name || "");
    const normalizedCode = this.normalizeAllergenCode(allergen.code || "");

    return normalizedName === "wheat"
      || normalizedName === "wheattriticale"
      || normalizedCode === "wheat";
  }

  async loadSavedAllergenConfiguration() {
    if (!this.recipe?.id) return;

    try {
      const response = await AllergenRepository.getStoredAllergenConfiguration(
        AllergenConfigurationTargetType.Recipe,
        this.recipe.id
      );

      this.selectedAllergens = this.isTrialUser
        ? []
        : this.normalizeSelectedAllergensWithDefinitions(response.data?.allergens || []);
      this.mayContainText = response.data?.mayContainText || {};
      this.hasAppliedBackOfPackageAiAnalysisFlag = false;
    }
    catch {
      this.selectedAllergens = [];
      this.mayContainText = {};
      this.hasAppliedBackOfPackageAiAnalysisFlag = false;
    }
  }

  async hydrateBackOfPackageAiAnalysis() {
    if (
      this.isTrialUser
      ||
      !this.isAllergenAiAddonActive
      || !this.recipe?.id
      || !this.accountState?.userDetails?.restaurantId
    ) {
      this.hasAppliedBackOfPackageAiAnalysisFlag = false;
      this.lastBackOfPackageAiAnalysisResult = null;
      if (this.isTrialUser) {
        this.selectedAllergens = [];
      }
      this.clearUnavailableAiIngredientFlags();
      return;
    }

    try {
      const response = await AllergenMLRepository.getSavedRecipeAnalysis({
        restaurantId: this.accountState.userDetails.restaurantId,
        recipeId: this.recipe.id,
        isCombinedRecipe: this.aiTargetType === "combined-recipe",
        countryId: this.recipe.nutritionCountry.id,
        nutrientSource: this.getAllergenAnalysisNutrientSource(),
      });

      const hasDetectedAllergen = this.analysisResultHasDetectedAllergen(response.data);

      this.hasAppliedBackOfPackageAiAnalysisFlag = hasDetectedAllergen;
      this.lastBackOfPackageAiAnalysisResult = response.data;
      this.recipe.hasAllergenAnalysis = true;
      this.recipe.hasAllergen = hasDetectedAllergen;
      this.selectedAllergens = this.normalizeSelectedAllergensWithDefinitions(this.selectedAllergens || []);
      this.$nextTick(() => {
        this.reapplyBackOfPackageAiVisualState();
      });
    }
    catch {
      this.hasAppliedBackOfPackageAiAnalysisFlag = false;
      this.lastBackOfPackageAiAnalysisResult = null;
      this.clearUnavailableAiIngredientFlags();
    }
  }

  clearUnavailableAiIngredientFlags() {
    const resetFlags = (items: IngredientDisplay[]) => {
      items.forEach((item) => {
        const currentState = item.allergenVisualState || "default";
        const isManualState = currentState === "user-added" || currentState === "user-confirmed";
        const shouldReset = currentState === "ai"
          || currentState === "user-removed"
          || (!isManualState && item.isAllergen);

        if (!shouldReset) {
          return;
        }

        this.$set(item, "isAllergen", false);
        this.$set(item, "allergenVisualState", "default");
      });
    };

    resetFlags(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    resetFlags(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);
  }

  async applyIngredientAiAllergens() {
    if (this.isApplyingIngredientAiAllergens) {
      return;
    }

    if (this.isTrialUser) {
      this.trialUpsellMessage = this.getAllergenAiUpsellMessage();
      this.showTrialUpsellModal = true;
      return;
    }

    if (!this.recipe?.id || !this.accountState?.userDetails?.restaurantId) {
      return;
    }

    this.isApplyingIngredientAiAllergens = true;

    this.shouldExpandAllergensConfiguration = false;
    await this.$nextTick();
    this.shouldExpandAllergensConfiguration = true;
    await this.$nextTick();

    this.ingredientAiAllergenApplyRequestToken += 1;
  }

  onIngredientAiDetectorRequestFinished() {
    this.isApplyingIngredientAiAllergens = false;
  }

  onBackOfPackageAiAnalysisApplied(result: MlAnalysisResponse) {
    const hasDetectedAllergen = this.analysisResultHasDetectedAllergen(result);

    this.hasAppliedBackOfPackageAiAnalysisFlag = hasDetectedAllergen;
    this.lastBackOfPackageAiAnalysisResult = result;
    this.recipe.hasAllergenAnalysis = true;
    this.recipe.hasAllergen = hasDetectedAllergen;
    this.applyAiAllergenFlagsToIngredientDisplays(result);
    this.debouncedFetchPreview();
  }

  reapplyBackOfPackageAiVisualState() {
    if (!this.lastBackOfPackageAiAnalysisResult) {
      return;
    }

    this.applyAiAllergenFlagsToIngredientDisplays(this.lastBackOfPackageAiAnalysisResult);
  }

  analysisResultHasDetectedAllergen(result: MlAnalysisResponse) {
    if ((result.ingredients || []).length > 0) {
      return result.ingredients.some((ingredientResult) =>
        this.analysisEntryHasDetectedAllergen(ingredientResult.allergens, ingredientResult.overrideMarkers || [])
      );
    }

    return this.analysisEntryHasDetectedAllergen(result.allergens || [], []);
  }

  analysisEntryHasDetectedAllergen(
    allergens: Array<{ id?: string | null }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean }> = []
  ) {
    const detectedCodes = new Set(
      allergens
        .map((allergen) => (allergen.id || "").trim().toLowerCase())
        .filter(Boolean)
    );

    for (const marker of overrideMarkers) {
      const code = (marker.allergenCode || "").trim().toLowerCase();

      if (!code) {
        continue;
      }

      if (marker.isAllergen) {
        detectedCodes.add(code);
      }
      else {
        detectedCodes.delete(code);
      }
    }

    return detectedCodes.size > 0;
  }

  applyAiAllergenFlagsToIngredientDisplays(result: MlAnalysisResponse) {
    const detectedByIngredientId = new Map<number, boolean>();
    const visualStateByIngredientId = new Map<number, IngredientAllergenVisualState>();
    const detectedBySubrecipeId = new Map<number, boolean>();
    const visualStateBySubrecipeId = new Map<number, IngredientAllergenVisualState>();

    for (const subrecipe of result.subrecipes || []) {
      if (!subrecipe.recipeId) {
        continue;
      }

      detectedBySubrecipeId.set(subrecipe.recipeId, Boolean(subrecipe.hasAllergen));
      visualStateBySubrecipeId.set(
        subrecipe.recipeId,
        subrecipe.hasAllergen ? "ai" : "default"
      );
    }

    for (const ingredientResult of result.ingredients || []) {
      const visualSignals = this.buildIngredientAllergenVisualSignals(
        ingredientResult.allergens,
        ingredientResult.overrideMarkers || []
      );
      const hasDetectedAllergen = visualSignals.hasDetectedAllergen;
      const visualState = this.getIngredientAllergenVisualStateFromSignals(visualSignals);
      const subrecipeId = this.extractSubrecipeIdFromLineage(ingredientResult.lineagePath);

      if (subrecipeId) {
        detectedBySubrecipeId.set(
          subrecipeId,
          Boolean(detectedBySubrecipeId.get(subrecipeId) || hasDetectedAllergen)
        );
        visualStateBySubrecipeId.set(
          subrecipeId,
          this.mergeIngredientVisualStates(visualStateBySubrecipeId.get(subrecipeId) ?? "default", visualState)
        );
      }

      const ingredientId = ingredientResult.ingredientId;

      if (!ingredientId) {
        continue;
      }

      detectedByIngredientId.set(
        ingredientId,
        Boolean(detectedByIngredientId.get(ingredientId) || hasDetectedAllergen)
      );
      visualStateByIngredientId.set(
        ingredientId,
        this.mergeIngredientVisualStates(visualStateByIngredientId.get(ingredientId) ?? "default", visualState)
      );
    }

    const applyFlags = (items: IngredientDisplay[]) => {
      items.forEach((item) => {
        const manualOverride = this.getManualIngredientAllergenOverride(item);

        if (manualOverride) {
          this.$set(item, "isAllergen", manualOverride.isAllergen);
          this.$set(item, "allergenVisualState", manualOverride.visualState);
          return;
        }

        if (item.recipeId) {
          this.$set(item, "isAllergen", detectedBySubrecipeId.get(item.recipeId) ?? false);
          this.$set(item, "allergenVisualState", visualStateBySubrecipeId.get(item.recipeId) ?? "default");
          return;
        }

        if (!item.ingredientId) {
          return;
        }

        this.$set(item, "isAllergen", detectedByIngredientId.get(item.ingredientId) ?? false);
        this.$set(item, "allergenVisualState", visualStateByIngredientId.get(item.ingredientId) ?? "default");
      });
    };

    applyFlags(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyFlags(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);
  }

  onIngredientAllergenManualOverrideChanged(payload: {
    ingredientId?: number;
    recipeId?: number;
    isAllergen: boolean;
    allergenVisualState?: IngredientAllergenVisualState;
  }) {
    const overrideKey = this.getIngredientAllergenOverrideKey(payload);
    if (!overrideKey) {
      return;
    }
    const visualState = payload.allergenVisualState || "default";
    if (this.shouldPersistManualIngredientAllergenOverride(visualState)) {
      this.manualIngredientAllergenOverrides.set(overrideKey, {
        isAllergen: payload.isAllergen,
        visualState,
      });
      return;
    }
    this.manualIngredientAllergenOverrides.delete(overrideKey);
  }

  getIngredientAllergenOverrideKey(item: { ingredientId?: number; recipeId?: number }) {
    if (item.ingredientId) {
      return `i_${item.ingredientId}`;
    }
    if (item.recipeId) {
      return `r_${item.recipeId}`;
    }
    return "";
  }

  getManualIngredientAllergenOverride(item: { ingredientId?: number; recipeId?: number }) {
    const overrideKey = this.getIngredientAllergenOverrideKey(item);
    return overrideKey
      ? this.manualIngredientAllergenOverrides.get(overrideKey)
      : undefined;
  }

  shouldPersistManualIngredientAllergenOverride(visualState: IngredientAllergenVisualState) {
    return visualState === "user-added"
      || visualState === "user-confirmed"
      || visualState === "user-removed";
  }

  captureSavedManualIngredientAllergenOverrides(items: IngredientDisplay[]) {
    this.manualIngredientAllergenOverrides.clear();

    items.forEach((item) => {
      const overrideKey = this.getIngredientAllergenOverrideKey(item);
      const visualState = item.allergenVisualState || "default";
      if (!overrideKey || !this.shouldPersistManualIngredientAllergenOverride(visualState)) {
        return;
      }
      this.manualIngredientAllergenOverrides.set(overrideKey, {
        isAllergen: Boolean(item.isAllergen),
        visualState,
      });
    });
  }

  extractSubrecipeIdFromLineage(lineagePath: string | null | undefined) {
    const match = (lineagePath || "").match(/Subrecipe\/(\d+)/i);
    return match ? Number(match[1]) : 0;
  }

  getIngredientAllergenVisualStateFromAnalysis(
    allergens: Array<{ id?: string | null }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean; wasAiDetected: boolean }> = []
  ): IngredientAllergenVisualState {
    return this.getIngredientAllergenVisualStateFromSignals(
      this.buildIngredientAllergenVisualSignals(allergens, overrideMarkers)
    );
  }

  buildIngredientAllergenVisualSignals(
    allergens: Array<{
      id?: string | null;
      source?: string | null;
      primaryEvidenceType?: string | null;
      evidence?: string[] | null;
    }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean; wasAiDetected: boolean }> = []
  ) {
    const activeCodes = new Set<string>();
    const aiActiveCodes = new Set<string>();
    const removedCodes = new Set<string>();

    for (const allergen of allergens || []) {
      const code = this.normalizeAnalysisSourceAllergenCode(allergen.id || "");

      if (!code) {
        continue;
      }

      activeCodes.add(code);

      if (!isUserOverridePrediction(allergen)) {
        aiActiveCodes.add(code);
      }

      removedCodes.delete(code);
    }

    for (const marker of overrideMarkers || []) {
      const code = this.normalizeAnalysisSourceAllergenCode(marker.allergenCode || "");

      if (!code) {
        continue;
      }

      if (marker.isAllergen) {
        activeCodes.add(code);
        removedCodes.delete(code);

        if (marker.wasAiDetected) {
          aiActiveCodes.add(code);
        }
      }
      else {
        activeCodes.delete(code);
        aiActiveCodes.delete(code);
        removedCodes.add(code);
      }
    }

    const activeConfiguredState = this.getPrioritizedConfiguredVisualState(Array.from(activeCodes));
    const removedConfiguredState = this.getPrioritizedConfiguredVisualState(Array.from(removedCodes));
    const hasConfiguredAiCode = activeConfiguredState === "ai";
    const hasConfiguredManualCode = activeConfiguredState === "user-added";
    const hasConfiguredRemovedCode = removedConfiguredState === "user-removed"
      || activeConfiguredState === "user-removed";
    const hasConfiguredState = activeConfiguredState !== "default" || removedConfiguredState !== "default";
    const hasExplicitAiEvidence = aiActiveCodes.size > 0;

    return {
      hasDetectedAllergen: activeCodes.size > 0,
      hasActiveAiDetectedAllergen: hasConfiguredAiCode || (hasExplicitAiEvidence && !hasConfiguredRemovedCode),
      hasManualAddedAllergen: !hasExplicitAiEvidence
        && (hasConfiguredManualCode || (!hasConfiguredState && activeCodes.size > 0)),
      hasManualRemovedAiAllergen: hasConfiguredRemovedCode || removedCodes.size > 0,
      configuredVisualState: activeConfiguredState !== "default" ? activeConfiguredState : removedConfiguredState
    };
  }

  getPrioritizedConfiguredVisualState(allergenCodes: string[] = []): IngredientAllergenVisualState {
    const states = allergenCodes
      .map((code) => this.getConfiguredAllergenVisualStateByCode(code))
      .filter((state) => state !== "default");

    if (states.includes("ai")) {
      return "ai";
    }

    if (states.includes("user-added")) {
      return "user-added";
    }

    if (states.includes("user-removed")) {
      return "user-removed";
    }

    return "default";
  }

  getIngredientAllergenVisualStateFromSignals(
    {
      hasDetectedAllergen,
      hasActiveAiDetectedAllergen,
      hasManualAddedAllergen,
      hasManualRemovedAiAllergen,
      configuredVisualState
    }: {
      hasDetectedAllergen: boolean;
      hasActiveAiDetectedAllergen: boolean;
      hasManualAddedAllergen: boolean;
      hasManualRemovedAiAllergen: boolean;
      configuredVisualState: IngredientAllergenVisualState;
    }
  ): IngredientAllergenVisualState {

    if (!hasDetectedAllergen && hasManualRemovedAiAllergen) {
      return "user-removed";
    }

    if (hasActiveAiDetectedAllergen) {
      return "ai";
    }

    if (hasDetectedAllergen && hasManualAddedAllergen) {
      return "user-added";
    }

    if (configuredVisualState === "user-removed") {
      return "user-removed";
    }

    if (configuredVisualState === "user-added") {
      return "user-added";
    }

    return "default";
  }

  mergeIngredientVisualStates(
    currentState: IngredientAllergenVisualState,
    nextState: IngredientAllergenVisualState
  ): IngredientAllergenVisualState {
    const statePriority: Record<IngredientAllergenVisualState, number> = {
      default: 0,
      "user-removed": 1,
      "user-added": 2,
      "user-confirmed": 2,
      ai: 3
    };

    return statePriority[nextState] > statePriority[currentState]
      ? nextState
      : currentState;
  }

  getIngredientConfiguredVisualState(
    allergens: Array<{ id?: string | null }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean; wasAiDetected: boolean }> = []
  ): IngredientAllergenVisualState {
    const states = new Set<IngredientAllergenVisualState>();
    const candidateCodes = [
      ...allergens.map((allergen) => allergen.id || ""),
      ...overrideMarkers.map((marker) => marker.allergenCode || "")
    ]
      .map((code) => this.normalizeAllergenCode(code))
      .filter(Boolean);

    for (const code of candidateCodes) {
      const configuredState = this.getConfiguredAllergenVisualStateByCode(code);

      if (configuredState !== "default") {
        states.add(configuredState);
      }
    }

    if (states.has("ai")) {
      return "ai";
    }

    if (states.has("user-added")) {
      return "user-added";
    }

    if (states.has("user-removed")) {
      return "user-removed";
    }

    return "default";
  }

  getConfiguredAllergenVisualStateByCode(allergenCode: string): IngredientAllergenVisualState {
    const configuredAllergens = (this.selectedAllergens || []).filter((allergen: any) => {
      if (!allergen || typeof allergen !== "object") {
        return false;
      }

      return this.normalizeAnalysisSourceAllergenCode(allergen.code || allergen.name || "") === allergenCode;
    }) as any[];

    if (!configuredAllergens.length) {
      return "default";
    }

    const states = new Set<IngredientAllergenVisualState>();

    for (const configuredAllergen of configuredAllergens) {
      const normalizedStatus = normalizeAllergenSelectionStatus(configuredAllergen.status);

      if (normalizedStatus === AllergenSelectionStatus.DetectedByAI) {
        states.add("ai");
        continue;
      }

      if (normalizedStatus === AllergenSelectionStatus.DetectedByAIDisabledManually) {
        states.add("user-removed");
        continue;
      }

      states.add("user-added");
    }

    if (states.has("ai")) {
      return "ai";
    }

    if (states.has("user-added")) {
      return "user-added";
    }

    if (states.has("user-removed")) {
      return "user-removed";
    }

    return "default";
  }

  normalizeAllergenCode(value: string | null | undefined) {
    return (value || "").trim().toLowerCase().replace(/[^a-z]/g, "");
  }

  getAllergenAnalysisNutrientSource(): NutrientSource {
    switch (this.recipe?.nutritionCountry?.id) {
    case NutritionLabelCountry.Canada:
      return NutrientSource.Canada;
    case NutritionLabelCountry.UK:
      return NutrientSource.UK;
    case NutritionLabelCountry.US:
    default:
      return NutrientSource.US;
    }
  }

  normalizeAnalysisSourceAllergenCode(value: string | null | undefined) {
    const normalizedCode = this.normalizeAllergenCode(value);

    if (this.recipe?.nutritionCountry?.id === NutritionLabelCountry.UK) {
      switch (normalizedCode) {
      case "gluten":
      case "cerealscontaininggluten":
      case "cerealsthatcontaingluten":
      case "wheat":
      case "triticale":
      case "wheatandtriticale":
      case "wheattriticale":
        return "gluten";
      default:
        return normalizedCode;
      }
    }

    return normalizedCode;
  }



  handleCautionaryQualification(state: boolean) {
    this.$emit("cautionaryEligibility", state);
  }

  handleTrialCardExpandAttempt(event: MouseEvent) {
    if (!this.isTrialUser) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    if (target.closest(".card-title-actions") || target.closest(".card-header-actions")) {
      return;
    }

    const cardTitle = target.closest(".card-title") as HTMLElement | null;

    if (!cardTitle) {
      return;
    }

    const chevron = cardTitle.querySelector(".chevron");
    const isExpanded = Boolean(chevron?.classList.contains("active"));

    // Show upsell when opening a collapsed card; allow click to propagate so card expands.
    if (!isExpanded) {
      this.showTrialUpsellModal = true;
    }
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  updateRecipeMetadataChange(isSupplement: boolean) {
    this.$emit("updateRecipeMetadata", isSupplement);
  }

  async updateLanguageSelection(language: LabelLanguage) {
    this.model.primaryLanguage = language;
    this.$emit("languageChange", language);
    await this.requestLabelConfigurations();
    await this.fetchPreview();
  }

  get countryKey() {
    return parseCountryKey(this.recipe.nutritionCountry.name);
  }
  async requestLabelConfigurations() {
    if (!this.selectedLabelSubType?.value) {
      this.requestedLabelConfigurations = [];
      return;
    }

    const response = await NutritionLabelRepository.getConfigurationOptionsForLabelV2(
      this.countryKey,
      this.selectedLabelSubType.value
    );

    const {
      freeTextFields = [],
      configurationOptions = []
    } = response.data as LabelConfigurationResponse;

    this.requestedLabelConfigurations = configurationOptions;

    const savedFreeTextFieldMap = new Map(
      (this.parsedModelData.freeTextFields ?? []).map((field) => [field.displayText, field.content || []])
    );
    const currentFreeTextFieldMap = new Map(
      (this.freeTextFields ?? []).map((field) => [field.displayText, field.content || []])
    );

    const mergedPlaceholders = freeTextFields.map<FreeTextPlaceholder>((field, index) => {
      const displayText = field as FreeTextAttributeType;
      const currentContent = currentFreeTextFieldMap.get(displayText);
      const savedContent = savedFreeTextFieldMap.get(displayText);

      return {
        id: index + 1,
        displayText,
        content: [...(currentContent ?? savedContent ?? [])],
      };
    });

    this.model.freeTextPlaceholders = mergedPlaceholders;
    this.freeTextFields = mergedPlaceholders;
    this.hasNormalizedFreeTextPlaceholders = true;
  }

  get freeTextConfiguration(): FreeTextFieldConfiguration {
    const rv: FreeTextFieldConfiguration = {};

    for (const placeholder of this.freeTextFields || []) {
      rv[placeholder.displayText] = placeholder.content;
    }

    return rv;
  }

  onFreeTextPlaceholdersUpdated(val: FreeTextPlaceholder[]) {
    this.freeTextFields = val;
    this.model.freeTextPlaceholders = val;
  }

  ingredientAndSubRecipeDisplaysInit() {
    this.subRecipeDisplays = this.labelIngredientConfiguration.getSubRecipeDisplays(this.recipe, this.subrecipeMeasurements);

    if(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.length == 0){
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList = this.labelIngredientConfiguration.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        true
      );
    }

    if(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.length == 0){
      this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList = this.labelIngredientConfiguration.getIngredientAndSubRecipeDisplays(
        this.recipe,
        this.subrecipeMeasurements,
        false
      );
    }

    this.reapplyBackOfPackageAiVisualState();
  }

  /**
   * Resets the display text for a specific subrecipe based on its ID.
   *
   * @param {number} subrecipeId - The unique identifier of the subrecipe to reset its display.
   * @return {void} This method does not return a value.
   */
  resetSubrecipeDisplay(subrecipeId: number) {
    const subrecipeDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.recipeId == subrecipeId
    );

    if (subrecipeDisplay) {
      const recipe = this.recipe.childRecipes.find((x) => x.recipe.id == subrecipeId);
      subrecipeDisplay.displayTextInPrimaryLanguage = buildComplexIngredientName(recipe.recipe);
    }
  }

  resetIngredientDisplay(ingredientId: number) {
    const combinedIngredientDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );
    const expandedIngredientDisplay = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.find(
      (x) => x.ingredientId == ingredientId
    );

    const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientId);

    if (combinedIngredientDisplay) {
      combinedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }

    if (expandedIngredientDisplay) {
      expandedIngredientDisplay.displayTextInPrimaryLanguage = buildSimpleIngredientName(recipeIngredient.ingredient);
    }
  }

  /**
   * Identifies and returns a list of ingredients or subrecipes whose display names
   * have been changed from their default or expected values.
   *
   * @return {Array<{type: 'ingredient' | 'subrecipe', id: number}>} An array of objects representing items with changed display names.
   * Each object contains the type ('ingredient' or 'subrecipe') and the corresponding ID of the item.
   */
  get getChangedDisplayNames() {
    const resettableDisplays: { type: 'ingredient' | 'subrecipe'; id: number }[] = [];

    for (const ingredientDisplay of this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList) {
      const isIngredient = Boolean(ingredientDisplay.ingredientId);

      if (isIngredient) {
        const recipeIngredient = this.recipe.ingredients.find((x) => x.ingredient.id == ingredientDisplay.ingredientId);

        if(recipeIngredient) { // Temp fix for missing ingredients
          if (ingredientDisplay.displayTextInPrimaryLanguage != buildSimpleIngredientName(recipeIngredient.ingredient)) {
            resettableDisplays.push({type: 'ingredient', id: ingredientDisplay.ingredientId});
          }
        }
      }
      else {
        const subrecipe = this.recipe.childRecipes.find((x) => x.recipe.id == ingredientDisplay.recipeId);

        if (ingredientDisplay.displayTextInPrimaryLanguage != buildComplexIngredientName(subrecipe.recipe)) {
          resettableDisplays.push({type: 'subrecipe', id: ingredientDisplay.recipeId});
        }
      }
    }

    return resettableDisplays;
  }

  async handleResetModel() {
    this.model.bopCautionaryPreview = "";
    this.model.hasValidCautionary = false;
  }

  debouncedFetchPreview() {
    debounce(this.fetchPreview, 1000, "back-of-package-config-label-update");
  }

  async toggleSubrecipeExpansion() {
    this.labelIngredientConfiguration.shouldExpandSubrecipes = !this.labelIngredientConfiguration.shouldExpandSubrecipes;

    if(this.labelIngredientConfiguration.shouldExpandSubrecipes){
      this.updateIngredientName();
    }
    else{
      this.copyIngredientNamesOver();
    }

    if (
      this.labelIngredientConfiguration.shouldExpandSubrecipes
      && !this.lastBackOfPackageAiAnalysisResult
      && !this.isTrialUser
      && this.isAllergenAiAddonActive
    ) {
      await this.hydrateBackOfPackageAiAnalysis();
    }
    else {
      this.reapplyBackOfPackageAiVisualState();
    }

    this.debouncedFetchPreview();
  }

  copyIngredientNamesOver(){
    this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayTextInPrimaryLanguage = expandedItem.displayTextInPrimaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].displayText = expandedItem.displayText;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].isAllergen = expandedItem.isAllergen;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenVisualState = expandedItem.allergenVisualState;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenFormattingStyle = expandedItem.allergenFormattingStyle;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenFormattingColor = expandedItem.allergenFormattingColor;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenFormattingHighlightColor = expandedItem.allergenFormattingHighlightColor;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenFormattingFontFamily = expandedItem.allergenFormattingFontFamily;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList[itemIndex].allergenFormattingFontSize = expandedItem.allergenFormattingFontSize;
      }
    });
  }

  updateIngredientName(){
    this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList.forEach((expandedItem) => {
      const itemIndex = this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList.findIndex((i) => i.ingredientId === expandedItem.ingredientId);

      if (itemIndex !== -1) {
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].displayTextInSecondaryLanguage = expandedItem.displayTextInSecondaryLanguage;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].isAllergen = expandedItem.isAllergen;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenVisualState = expandedItem.allergenVisualState;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenFormattingStyle = expandedItem.allergenFormattingStyle;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenFormattingColor = expandedItem.allergenFormattingColor;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenFormattingHighlightColor = expandedItem.allergenFormattingHighlightColor;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenFormattingFontFamily = expandedItem.allergenFormattingFontFamily;
        this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList[itemIndex].allergenFormattingFontSize = expandedItem.allergenFormattingFontSize;
      }
    });
  }

  get selectedIngredientAllergenFormattingStyle(): IngredientAllergenFormattingStyle {
    const styles = (this.ingredientAndSubRecipeDisplays || []).map(
      (item) => item.allergenFormattingStyle || "bold"
    );

    if (styles.length === 0) {
      return "bold";
    }

    return styles.every((style) => style === styles[0])
      ? styles[0]
      : "bold";
  }

  get selectedIngredientAllergenFormattingColor(): string {
    const colors = (this.ingredientAndSubRecipeDisplays || []).map(
      (item) => item.allergenFormattingColor || DefaultIngredientAllergenFormattingColor
    );

    if (colors.length === 0) {
      return DefaultIngredientAllergenFormattingColor;
    }

    return colors.every((color) => color === colors[0])
      ? colors[0]
      : DefaultIngredientAllergenFormattingColor;
  }

  get selectedIngredientAllergenFormattingHighlightColor(): string {
    const colors = (this.ingredientAndSubRecipeDisplays || []).map(
      (item) => item.allergenFormattingHighlightColor || DefaultIngredientAllergenHighlightColor
    );

    if (colors.length === 0) {
      return DefaultIngredientAllergenHighlightColor;
    }

    return colors.every((color) => color === colors[0])
      ? colors[0]
      : DefaultIngredientAllergenHighlightColor;
  }

  get selectedIngredientAllergenFormattingFontFamily(): IngredientAllergenFormattingFontFamily {
    const fontFamilies = (this.ingredientAndSubRecipeDisplays || []).map(
      (item) => item.allergenFormattingFontFamily || DefaultIngredientAllergenFontFamily
    );

    if (fontFamilies.length === 0) {
      return DefaultIngredientAllergenFontFamily;
    }

    return fontFamilies.every((fontFamily) => fontFamily === fontFamilies[0])
      ? fontFamilies[0]
      : DefaultIngredientAllergenFontFamily;
  }

  get selectedIngredientAllergenFormattingFontSize(): IngredientAllergenFormattingFontSize {
    const fontSizes = (this.ingredientAndSubRecipeDisplays || []).map(
      (item) => item.allergenFormattingFontSize || DefaultIngredientAllergenFontSize
    );

    if (fontSizes.length === 0) {
      return DefaultIngredientAllergenFontSize;
    }

    return fontSizes.every((fontSize) => fontSize === fontSizes[0])
      ? fontSizes[0]
      : DefaultIngredientAllergenFontSize;
  }

  updateIngredientAllergenFormattingStyle(style: IngredientAllergenFormattingStyle) {
    const applyStyle = (displays: IngredientDisplay[]) => {
      displays.forEach((display) => {
        this.$set(display, "allergenFormattingStyle", style);

        if (style === "color" && !display.allergenFormattingColor) {
          this.$set(display, "allergenFormattingColor", DefaultIngredientAllergenFormattingColor);
        }

        if (style === "background-color" && !display.allergenFormattingHighlightColor) {
          this.$set(display, "allergenFormattingHighlightColor", DefaultIngredientAllergenHighlightColor);
        }

        if (style === "font-family" && !display.allergenFormattingFontFamily) {
          this.$set(display, "allergenFormattingFontFamily", DefaultIngredientAllergenFontFamily);
        }

        if (style === "font-size" && !display.allergenFormattingFontSize) {
          this.$set(display, "allergenFormattingFontSize", DefaultIngredientAllergenFontSize);
        }
      });
    };

    applyStyle(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyStyle(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);

    this.debouncedFetchPreview();
  }

  updateIngredientAllergenFormattingColor(color: string) {
    const applyColor = (displays: IngredientDisplay[]) => {
      displays.forEach((display) => {
        this.$set(display, "allergenFormattingColor", color || DefaultIngredientAllergenFormattingColor);
      });
    };

    applyColor(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyColor(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);

    this.debouncedFetchPreview();
  }

  updateIngredientAllergenFormattingHighlightColor(color: string) {
    const applyColor = (displays: IngredientDisplay[]) => {
      displays.forEach((display) => {
        this.$set(display, "allergenFormattingHighlightColor", color || DefaultIngredientAllergenHighlightColor);
      });
    };

    applyColor(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyColor(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);

    this.debouncedFetchPreview();
  }

  updateIngredientAllergenFormattingFontFamily(fontFamily: IngredientAllergenFormattingFontFamily) {
    const applyFontFamily = (displays: IngredientDisplay[]) => {
      displays.forEach((display) => {
        this.$set(display, "allergenFormattingFontFamily", fontFamily || DefaultIngredientAllergenFontFamily);
      });
    };

    applyFontFamily(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyFontFamily(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);

    this.debouncedFetchPreview();
  }

  updateIngredientAllergenFormattingFontSize(fontSize: IngredientAllergenFormattingFontSize) {
    const applyFontSize = (displays: IngredientDisplay[]) => {
      displays.forEach((display) => {
        this.$set(display, "allergenFormattingFontSize", fontSize || DefaultIngredientAllergenFontSize);
      });
    };

    applyFontSize(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList);
    applyFontSize(this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList);

    this.debouncedFetchPreview();
  }

  get renderModel() {
    const isSupplementLabel = isSupplementLabelTemplate(
      this.selectedLabelSubType?.value,
      this.preselectedLabelType
    );
    const coreNutrients = (this.labelConfiguration.selectedCoreNutrients || []).filter(
      (n) => typeof n === "number"
    );
    const supplementNutrients = (this.labelConfiguration.selectedSupplementNutrients || []).filter(
      (n) => typeof n === "number"
    );
    const availableVoluntaryNutrients = getVoluntaryNutrients(
      this.recipe.nutritionCountry.id,
      this.roundingRuleIndex,
      this.selectedLabelSubType?.value
    );
    const voluntaryNutrients = (this.labelConfiguration.selectedVoluntaryNutrients || []).filter(
      (n) => typeof n === "number" && availableVoluntaryNutrients.includes(n)
    );

    return {
      country: this.countryKey,
      labelName: this.selectedLabelSubType?.value || "",
      renderModel: {
        isFsisLabel: this.preselectedLabelType === LabelTypeMetadata.FSIS,
        language: this.model.primaryLanguage,
        shouldDisplaySugarAlcohol: this.labelConfiguration.shouldDisplaySugarAlcohol || false,
        shouldDisplayNutrientsSideBySide: !isSupplementLabel && (this.labelConfiguration.shouldDisplayNutrientsSideBySide || false),
        shouldDisplayCalories449Text: !isSupplementLabel && (this.labelConfiguration.shouldDisplayCalories449Text || false),
        includeTextualIndicators: this.labelConfiguration.includeTextualIndicators || false,
        showShortFooter: this.labelConfiguration.showShortFooter || false,
        showSideBySidePanel: !isSupplementLabel && (this.labelConfiguration.showSideBySidePanel || false),

        switchServingSizeText: !isSupplementLabel && (this.labelConfiguration.switchServingSizeText || false),
        showCalorieFromFatText: !isSupplementLabel && (this.labelConfiguration.showCalorieFromFatText || false),
        switchCalorieBreakdownFootnote: !isSupplementLabel && (this.labelConfiguration.switchCalorieBreakdownFootnote || false),
        hideTotalText: !isSupplementLabel && (this.labelConfiguration.hideTotalText || false),
        useNormalServingSizeFont: !isSupplementLabel && (this.labelConfiguration.useNormalServingSizeFont || false),
        useMcgUnit: this.labelConfiguration.useMcgUnit || false,
        displayValueforCoreNutrients: !isSupplementLabel && (this.labelConfiguration.displayValueforCoreNutrients || false),

        switchToElectrolyteMode: this.labelConfiguration.switchToElectrolyteMode || false,
        hideOtherText: this.labelConfiguration.hideOtherText || false,

        widthCm: this.labelConfiguration.widthCm || 7,
        freeTextFields: this.freeTextConfiguration,
        recipeId: this.recipe.id,
        recipes: this.labelConfiguration.recipes || [],

        coreNutrients: coreNutrients,
        supplementNutrients: supplementNutrients,
        voluntaryNutrients: voluntaryNutrients,
        proprietaryBlends: this.labelConfiguration.proprietaryBlends || [],

        showProteinDV: !isSupplementLabel && (this.labelConfiguration.showProteinDV || false),
        showSupplementProteinDV: this.labelConfiguration.showSupplementProteinDV,
        hideCanadaSupplementFootnote: this.labelConfiguration.hideCanadaSupplementFootnote,
        showCarbsDV: this.labelConfiguration.showCarbsDV || false,
        showThcLogo: this.labelConfiguration.showThcLogo,
        includeRi: this.labelConfiguration.includeRi,
        includeSaltFootnote: this.labelConfiguration.includeSaltFootnote,
        includePerServingAnalysis: this.labelConfiguration.includePerServingAnalysis,
        includeTrafficLightColoring: this.labelConfiguration.includeTrafficLightColoring,
        includeMonoPoly: this.labelConfiguration.includeMonoPoly,
        includeOmegas: this.labelConfiguration.includeOmegas,
        hideBorders: this.labelConfiguration.hideBorders || false,

        useCondensedFont: this.labelConfiguration.useCondensedFont,
        useCondensedFormat: this.labelConfiguration.useCondensedFormat,
        combineVoluntaryNutrients: this.labelConfiguration.combineVoluntaryNutrients,
        useInlineFootnote: this.labelConfiguration.useInlineFootnote,
        useParForFrenchServingSize: this.labelConfiguration.useParForFrenchServingSize,

        brandingConfiguration: {
          showLogo: this.brandingConfiguration.showLogo,
          logoPosition: this.brandingConfiguration.logoPosition,
          textTop: this.brandingConfiguration.textTop,
          textBottom: this.brandingConfiguration.textBottom,
          showImage: this.brandingConfiguration.showImage,
          imagePosition: this.brandingConfiguration.imagePosition
        },

        belowLabelConfiguration: {
          allergens: this.selectedAllergens || [],
          mayContain: this.normalizeMayContain(this.mayContainText) || [],
          ingredientDisplayModels: this.labelIngredientConfiguration.shouldExpandSubrecipes
            ? this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
            : this.labelIngredientConfiguration.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
          shouldDisplayIngredients: this.labelIngredientConfiguration.includeIngredients,
          isCommaSeparated: true,
          shouldDisplayColon: true,
          shouldExpandSubrecipes: this.labelIngredientConfiguration.shouldExpandSubrecipes,
          dietaryLabels: this.selectedDietaryTypes,
          thresholdPercent: this.labelIngredientConfiguration.thresholdPercent
        }
      }
    };
  }

  // normalizeMayContain(value: Record<string, string[]>): string[][] {
  //   return Object.values(value).filter((v) => v.length > 0);
  // }

  normalizeMayContain(value: Record<string, string[]>): string[][] {
    return Object.values(value)
      .map((v) => [...v])
      .filter((v) => v.length > 0);
  }

  @Provide("bopFetchPreview")
  async fetchPreview(downloadFormat = NutritionLabelDownloadFileFormat.png) {
    try {
      return await this.fetchPreviewV2(
        {
          format: downloadFormat,
          ...this.renderModel
        },
        ExportLabelView.BOP
      );
    }

    catch (error) {
      console.error("Failed to fetch preview:", error);
    }
  }
}
